import React, { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import TeamPyramid from '../components/TeamPyramid/TeamPyramid';
import Reveal from '../components/Reveal/Reveal';
import './About.css';
import { motion } from 'motion/react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import api from '../services/api';

const ARRIVAL_DISTRIBUTION = [
  { id: 'paris', label: 'Paris', value: 33, color: '#84c7be' },
  { id: 'lyon', label: 'Lyon', value: 9.5, color: '#e3df96' },
  { id: 'lyon-alt', label: 'Lyon', value: 9, color: '#a9a5c3' },
  { id: 'toulouse', label: 'Toulouse', value: 6, color: '#f17d6f' },
  { id: 'strasbourg', label: 'Strasbourg', value: 5.5, color: '#6c9ec2' },
  { id: 'marseille', label: 'Marseille', value: 4, color: '#f2b35f' },
  { id: 'nantes', label: 'Nantes', value: 3.7, color: '#99c563' },
  { id: 'bordeaux', label: 'Bordeaux', value: 2.5, color: '#e8b9d5' },
  { id: 'autres', label: 'Autres', value: 27, color: '#c8c8c8' },
];

const MEMBERS_MILESTONES = [
  { year: 2018, members: 1400 },
  { year: 2019, members: 1650 },
  { year: 2020, members: 2300 },
  { year: 2021, members: 3700 },
  { year: 2022, members: 4800 },
  { year: 2023, members: 5600 },
  { year: 2024, members: 6500 },
  { year: 2025, members: 7000 },
  { year: 2026, members: 7500 },
];

const DONUT_SIZE = 320;
const DONUT_CENTER = DONUT_SIZE / 2;
const DONUT_RADIUS = 84;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;

const MEMBERS_CHART = {
  width: 420,
  height: 500,
  marginTop: 22,
  marginRight: 18,
  marginBottom: 40,
  marginLeft: 46,
};

const MEMBERS_AXIS_MIN = 1000;
const MEMBERS_AXIS_MAX = 8000;
const MEMBERS_AXIS_TICKS = [1000, 2000, 3000, 4000, 5000, 6000, 7000, 8000];
const FRANCE_CENTER = { lat: 46.481056, lng: 2.392072 };
const MAP_ZOOM = 5;
const PIN_SIZE = 45;
const PIN_OFFSET = Math.round(PIN_SIZE / 2);
const DEFAULT_REP_IMAGE = '/logo.png';
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const MAPSTYLE = [
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [
      { color: '#e9e9e9' },
      { lightness: 17 },
    ],
  },
  {
    featureType: 'landscape',
    elementType: 'geometry',
    stylers: [
      { color: '#f5f5f5' },
      { lightness: 20 },
    ],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.fill',
    stylers: [
      { color: '#ffffff' },
      { lightness: 17 },
    ],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [
      { color: '#ffffff' },
      { lightness: 29 },
      { weight: 0.2 },
    ],
  },
  {
    featureType: 'road.arterial',
    elementType: 'geometry',
    stylers: [
      { color: '#ffffff' },
      { lightness: 18 },
    ],
  },
  {
    featureType: 'road.local',
    elementType: 'geometry',
    stylers: [
      { color: '#ffffff' },
      { lightness: 16 },
    ],
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [
      { color: '#f5f5f5' },
      { lightness: 21 },
    ],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [
      { color: '#dedede' },
      { lightness: 21 },
    ],
  },
  {
    elementType: 'labels.text.stroke',
    stylers: [
      { visibility: 'on' },
      { color: '#ffffff' },
      { lightness: 16 },
    ],
  },
  {
    elementType: 'labels.text.fill',
    stylers: [
      { saturation: 36 },
      { color: '#333333' },
      { lightness: 40 },
    ],
  },
  {
    elementType: 'labels.icon',
    stylers: [
      { visibility: 'off' },
    ],
  },
  {
    featureType: 'transit',
    elementType: 'geometry',
    stylers: [
      { color: '#f2f2f2' },
      { lightness: 19 },
    ],
  },
  {
    featureType: 'administrative',
    elementType: 'geometry.fill',
    stylers: [
      { color: '#fefefe' },
      { lightness: 20 },
    ],
  },
  {
    featureType: 'administrative',
    elementType: 'geometry.stroke',
    stylers: [
      { color: '#fefefe' },
      { lightness: 17 },
      { weight: 1.2 },
    ],
  },
];

const easeOutCubic = (value) => 1 - Math.pow(1 - value, 3);

const formatPlainInteger = (value) => `${Math.max(0, Math.round(Number(value) || 0))}`;

const formatDistributionValue = (value) => {
  const numeric = Number(value) || 0;
  if (Number.isInteger(numeric)) return `${numeric}`;
  return numeric.toFixed(1).replace(/\.0$/, '');
};

const buildSmoothPath = (points) => {
  if (!Array.isArray(points) || points.length === 0) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  if (points.length === 2) return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;

  const segmentCount = points.length - 1;
  const deltas = new Array(segmentCount);
  const spans = new Array(segmentCount);
  const tangents = new Array(points.length);

  for (let index = 0; index < segmentCount; index += 1) {
    const span = points[index + 1].x - points[index].x || 1;
    spans[index] = span;
    deltas[index] = (points[index + 1].y - points[index].y) / span;
  }

  tangents[0] = deltas[0];
  for (let index = 1; index < segmentCount; index += 1) {
    const previousSlope = deltas[index - 1];
    const nextSlope = deltas[index];

    if (
      Math.abs(previousSlope) < 0.000001 ||
      Math.abs(nextSlope) < 0.000001 ||
      Math.sign(previousSlope) !== Math.sign(nextSlope)
    ) {
      tangents[index] = 0;
    } else {
      tangents[index] = (previousSlope + nextSlope) / 2;
    }
  }
  tangents[points.length - 1] = deltas[segmentCount - 1];

  for (let index = 0; index < segmentCount; index += 1) {
    const slope = deltas[index];
    if (Math.abs(slope) < 0.000001) {
      tangents[index] = 0;
      tangents[index + 1] = 0;
      continue;
    }

    const a = tangents[index] / slope;
    const b = tangents[index + 1] / slope;
    const norm = (a * a) + (b * b);
    if (norm > 9) {
      const scale = 3 / Math.sqrt(norm);
      tangents[index] = scale * a * slope;
      tangents[index + 1] = scale * b * slope;
    }
  }

  let path = `M ${points[0].x} ${points[0].y}`;
  for (let index = 0; index < segmentCount; index += 1) {
    const current = points[index];
    const next = points[index + 1];
    const span = spans[index];
    const control1X = current.x + (span / 3);
    const control1Y = current.y + ((tangents[index] * span) / 3);
    const control2X = next.x - (span / 3);
    const control2Y = next.y - ((tangents[index + 1] * span) / 3);
    path += ` C ${control1X} ${control1Y} ${control2X} ${control2Y} ${next.x} ${next.y}`;
  }
  return path;
};

const buildAreaPath = (points, baselineY) => {
  if (!Array.isArray(points) || points.length === 0) return '';
  const curve = buildSmoothPath(points);
  const start = points[0];
  const end = points[points.length - 1];
  return `${curve} L ${end.x} ${baselineY} L ${start.x} ${baselineY} Z`;
};

const useRevealTrigger = ({ threshold = 0.32, rootMargin = '0px 0px -12% 0px', once = true } = {}) => {
  const elementRef = useRef(null);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasEntered, setHasEntered] = useState(false);

  useEffect(() => {
    const node = elementRef.current;
    if (!node) return;

    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsIntersecting(true);
      setHasEntered(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting) {
          setIsIntersecting(true);
          setHasEntered(true);
          if (once) observer.unobserve(entry.target);
          return;
        }

        if (!once) setIsIntersecting(false);
      },
      { threshold, rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);

  return {
    ref: elementRef,
    isVisible: once ? hasEntered : isIntersecting,
  };
};

const useProgressAnimation = (
  shouldStart,
  { duration = 1000, delay = 0 } = {}
) => {
  const [progress, setProgress] = useState(0);
  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (!shouldStart || hasStartedRef.current) return;
    hasStartedRef.current = true;

    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setProgress(1);
      return;
    }

    let frameId = null;
    let timeoutId = null;
    const totalDuration = Math.max(1, Number(duration) || 1);
    const launchDelay = Math.max(0, Number(delay) || 0);

    timeoutId = window.setTimeout(() => {
      const startTime = performance.now();

      const tick = (timestamp) => {
        const elapsed = timestamp - startTime;
        const ratio = Math.min(1, elapsed / totalDuration);
        setProgress(easeOutCubic(ratio));
        if (ratio < 1) {
          frameId = window.requestAnimationFrame(tick);
        }
      };

      frameId = window.requestAnimationFrame(tick);
    }, launchDelay);

    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }, [delay, duration, shouldStart]);

  return progress;
};

const useCountUp = (
  target,
  shouldStart,
  { duration = 1200, delay = 0 } = {}
) => {
  const [value, setValue] = useState(0);
  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (!shouldStart || hasStartedRef.current) return;
    hasStartedRef.current = true;

    const nextTarget = Math.max(0, Number(target) || 0);
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setValue(nextTarget);
      return;
    }

    let frameId = null;
    let timeoutId = null;
    const totalDuration = Math.max(1, Number(duration) || 1);
    const launchDelay = Math.max(0, Number(delay) || 0);

    timeoutId = window.setTimeout(() => {
      const startTime = performance.now();

      const tick = (timestamp) => {
        const elapsed = timestamp - startTime;
        const ratio = Math.min(1, elapsed / totalDuration);
        const eased = easeOutCubic(ratio);
        setValue(nextTarget * eased);
        if (ratio < 1) {
          frameId = window.requestAnimationFrame(tick);
        }
      };

      frameId = window.requestAnimationFrame(tick);
    }, launchDelay);

    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }, [delay, duration, shouldStart, target]);

  return value;
};

const useTweenedList = (targetValues, duration = 640) => {
  const [values, setValues] = useState(targetValues);
  const previousValuesRef = useRef(targetValues);

  useEffect(() => {
    previousValuesRef.current = values;
  }, [values]);

  useEffect(() => {
    const source = previousValuesRef.current;
    if (!Array.isArray(targetValues) || source.length !== targetValues.length) {
      setValues(targetValues);
      previousValuesRef.current = targetValues;
      return;
    }

    if (
      source.every((value, index) => Math.abs(value - targetValues[index]) < 0.0001)
    ) {
      return;
    }

    let frameId = null;
    const totalDuration = Math.max(1, Number(duration) || 1);
    const startTime = performance.now();

    const tick = (timestamp) => {
      const elapsed = timestamp - startTime;
      const ratio = Math.min(1, elapsed / totalDuration);
      const eased = easeOutCubic(ratio);
      const nextValues = targetValues.map((value, index) => {
        const start = source[index] || 0;
        return start + (value - start) * eased;
      });
      setValues(nextValues);
      if (ratio < 1) {
        frameId = window.requestAnimationFrame(tick);
      }
    };

    frameId = window.requestAnimationFrame(tick);
    return () => {
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }, [duration, targetValues]);

  return values;
};

const apiBaseUrl = axios.defaults.baseURL || '';
const resolveMediaUrl = (path) => {
  if (!path) return null;
  const raw = String(path).trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  const normalized = raw.startsWith('/') ? raw : `/${raw}`;
  if (!normalized.startsWith('/uploads/')) return normalized;
  const base = String(apiBaseUrl || '').replace(/\/$/, '');
  return base ? `${base}${normalized}` : normalized;
};

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const resolveRepresentantImage = (image) => resolveMediaUrl(image) || DEFAULT_REP_IMAGE;

const RepresentantCard = (id, name, image, position, phone, email) => {
  const safeId = escapeHtml(id);
  const safeName = escapeHtml(name);
  const safePosition = escapeHtml(position);
  const safePhone = escapeHtml(phone);
  const safeEmail = escapeHtml(email);
  const resolvedImage = resolveRepresentantImage(image);

  return `
  <div class="representants scroll-item col-lg-12 col-md-12 col-sm-12 col-xs-12" target="rep-${safeId}">
  <div class="col-lg-12 col-md-12 col-sm-12 col-xs-12">
        <div class="card selected">
            <div class="name-info">
              ${safeName}
              <div selector="rep-${safeId}" class="scroll-closer">
                <img src="/images/icons/close.svg" alt="Close">
              </div>
            </div>
            <div class="contact-info">
                <div class="col-lg-3 col-md-3 col-sm-12 col-xs-12">
                  <img src="${resolvedImage}" alt="${safeName}" />
                </div>
                <div class="col-lg-9 info col-md-9 col-sm-12 col-xs-12">
                    <div class="position">
                      <span>${safePosition}</span>
                    </div>
                    ${phone ?
                      `<div class="phone">
                        <a href="tel:${safePhone}">${safePhone}</a>
                      </div>` : ''
                    }
                    ${email ?
                      `<div class="email">
                        <a href="mailto:${safeEmail}">${safeEmail}</a>
                      </div>` : ''
                    }
                </div>
            </div>
        </div>
    </div>
</div>
  `;
};

const Representant = (id, _city, image, name, position, phone, email, withCard = true, teamColor = '#607ad7') => {
  const safeId = escapeHtml(id);
  const safeName = escapeHtml(name);
  const resolvedImage = resolveRepresentantImage(image);

  return `
      <div selector="rep-${safeId}" class="pin scroll-selector col-lg-3 col-md-3">
        <img style="border-color: ${teamColor}" src="${resolvedImage}" alt="${safeName}" />
      </div>
      ${ !withCard ? '' : `
        ${RepresentantCard(id, name, image, position, phone, email)}`
      }
  `;
};

const normalizeRepresentant = (row = {}) => {
  const latitude = Number(row.latitude);
  const longitude = Number(row.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

  return {
    id: row.id ?? row.slug ?? `${row.city || 'rep'}-${latitude}-${longitude}`,
    city: String(row.city || '').trim(),
    image: row.image ?? row.image_url ?? row.image_path ?? row.photo ?? null,
    name: String(row.name || row.full_name || row.fullName || row.title || 'ADEEF').trim(),
    position: String(row.position || row.role || '').trim(),
    phone: row.phone ?? row.phone_number ?? null,
    email: row.email ?? null,
    latitude,
    longitude,
  };
};

let googleMapsLoaderPromise = null;

const loadGoogleMaps = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Window is not available.'));
  }

  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (!GOOGLE_MAPS_API_KEY) {
    return Promise.reject(new Error('Missing Google Maps API key.'));
  }

  if (googleMapsLoaderPromise) return googleMapsLoaderPromise;

  googleMapsLoaderPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector('script[data-google-maps="true"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.google.maps));
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Google Maps.')));
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}&loading=async`;
    script.async = true;
    script.defer = true;
    script.dataset.googleMaps = 'true';
    script.onload = () => resolve(window.google.maps);
    script.onerror = () => {
      googleMapsLoaderPromise = null;
      reject(new Error('Failed to load Google Maps.'));
    };
    document.head.appendChild(script);
  });

  return googleMapsLoaderPromise;
};

const createCustomMapPinClass = (googleMaps) => {
  function CustomMapPin(position, map, representant, options, width) {
    this.position = position;
    this.map = map;
    this.representant = representant;
    this.options = options;
    this.width = width;
    this.elHTML = '';
    this.el = null;
    this.setMap(map);
  }

  CustomMapPin.prototype = new googleMaps.OverlayView();

  CustomMapPin.prototype.onAdd = function() {
    this.el = document.createElement('div');
    this.el.classList.add('representants-container');
    this.el.innerHTML = Representant(
      this.representant.id,
      this.representant.city,
      this.representant.image,
      this.representant.name,
      this.representant.position,
      this.representant.phone,
      this.representant.email,
      this.options.withCard
    );

    this.getPanes().overlayMouseTarget.appendChild(this.el);
  };

  CustomMapPin.prototype.draw = function() {
    if (!this.el) return;
    const point = this.getProjection().fromLatLngToDivPixel(this.position);
    if (!point) return;

    if (googleMaps.OverlayView.preventMapHitsFrom) {
      googleMaps.OverlayView.preventMapHitsFrom(this.el);
    }

    this.el.style.left = `${point.x - this.width}px`;
    this.el.style.top = `${point.y - this.width * 2}px`;
  };

  CustomMapPin.prototype.onRemove = function() {
    if (this.el?.parentNode) {
      this.el.parentNode.removeChild(this.el);
    }
    this.el = null;
  };

  return CustomMapPin;
};

const StatsFranceMap = () => {
  const mapRef = useRef(null);
  const pinsRef = useRef([]);
  const [mapError, setMapError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const clearPins = () => {
      pinsRef.current.forEach((pin) => {
        if (pin?.setMap) pin.setMap(null);
      });
      pinsRef.current = [];
    };

    const initMap = async () => {
      if (!mapRef.current) return;
      let googleMaps = null;

      try {
        googleMaps = await loadGoogleMaps();
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load stats map:', error);
          setMapError(error?.message || 'load-error');
        }
        return;
      }

      if (cancelled || !mapRef.current || !googleMaps || typeof googleMaps.Map !== 'function') {
        if (!cancelled) {
          setMapError('Google Maps failed to initialize.');
        }
        return;
      }

      const mapPresence = new googleMaps.Map(mapRef.current, {
        center: FRANCE_CENTER,
        styles: MAPSTYLE,
        zoom: MAP_ZOOM,
        disableDefaultUI: true,
        gestureHandling: 'none',
        zoomControl: false,
      });

      const CustomMapPin = createCustomMapPinClass(googleMaps);

      try {
        const response = await api.teamMembers.list();
        const rows = Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
            ? response.data
            : [];

        rows
          .map(normalizeRepresentant)
          .filter(Boolean)
          .forEach((representant) => {
            const markerPos = new googleMaps.LatLng(representant.latitude, representant.longitude);
            const pin = new CustomMapPin(
              markerPos,
              mapPresence,
              representant,
              { withCard: false },
              PIN_OFFSET
            );
            pinsRef.current.push(pin);
          });
      } catch (error) {
        console.error('Failed to load stats map pins:', error);
      }
    };

    initMap();

    return () => {
      cancelled = true;
      clearPins();
    };
  }, []);

  return (
    <div className="stats-map-card" role="group" aria-label="Carte de la presence ADEEF en France">
      <div className="stats-map-frame">
        <div id="map-presence" ref={mapRef} />
        {mapError && (
          <div className="stats-map-fallback" role="status">
            {mapError.includes('Missing Google Maps API key')
              ? 'La carte ne peut pas s\'afficher sans cle Google Maps.'
              : 'La carte est indisponible pour le moment.'}
          </div>
        )}
      </div>
    </div>
  );
};

const About = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [modalSlide, setModalSlide] = useState(null);
  const [closingModal, setClosingModal] = useState(false);
  const [isCarouselInView, setIsCarouselInView] = useState(false);
  const [remoteSlides, setRemoteSlides] = useState([]);
  const [hiddenArrivalRegions, setHiddenArrivalRegions] = useState(() => new Set());
  const [activeMilestoneIndex, setActiveMilestoneIndex] = useState(null);
  const [activeArrivalSlice, setActiveArrivalSlice] = useState(null);
  const closeTimerRef = useRef(null);
  const bodyOverflowRef = useRef(null);
  const carouselAnchorRef = useRef(null);
  const carouselSectionRef = useRef(null);
  const arrivalsPanel = useRevealTrigger();
  const citiesPanel = useRevealTrigger();
  const membersPanel = useRevealTrigger();

  const fallbackSlides = useMemo(
    () => [
      {
        id: 1,
        title: 'Créée par et pour les étudiants égyptiens',
        summary:
          'Depuis 2011, l\'ADEEF accompagne les étudiants égyptiens dans leur intégration universitaire et professionnelle en France grâce à un réseau solidaire.',
        image: '/img/Group 1.jpeg',
        content: `En 2011, un groupe des jeunes étudiants égyptiens ayant vécu et étudié en France, décide de créer une association à but non lucratif et apolitique en France : L'ADEEF. Cette association a pour but d'aider les étudiants égyptiens - étudiant ou souhaitant étudier en France - afin d'assurer dans un premier temps leur intégration dans le système universitaire français et de faciliter, dans un second temps, leur intégration dans le marché professionnel.

L'ADEEF a été donc déclarée officiellement auprès de la préfecture sous la loi 1901 avec un statut publié dans le journal officiel en décembre 2011.

L'ADEEF s'est de même fixée pour objectif la contribution au développement scientifique et culturel de l'Egypte et le renforcement des relations franco-égyptiennes à travers la formation d'un fort réseau solidaire, constitué principalement des étudiants et jeunes professionnels en France.`,
        founders: [
          'Karim EL-CHAZLI',
          'Ahmed HABIB',
          'Yehia SHAHINE',
          'Saker ELNOUR',
          'Mootaz ALLAM',
          'Hesham ABOUTALEB',
          'Rim ISMAIL',
          'Nanice KHALED',
        ],
      },
      {
        id: 2,
        title: 'Un réseau fort et solidaire',
        summary:
          'Un talent pool francophone, anglophone et arabophone qui partage retours d\'expérience, conseils concrets et opportunités pour accélérer les parcours.',
        image: '/img/Exchange 1.jpeg',
        content: `Le corps de l'ADEEF est constitué principalement d'étudiants et de jeunes professionnels de très hauts potentiels qui sont allés en France afin de poursuivre leurs études supérieures dans différents domaines, dont les diplômés occupent aujourd'hui des postes à haute responsabilité, que ce soit en France ou en Egypte.

Majoritairement issu des écoles francophones en Egypte et maîtrisant couramment au moins trois langues, à savoir le français, l'anglais et l'arabe, ce réseau représente un réel "Talent Pool" qui s'entraide et fournit un retour d'expérience assez riche dans un esprit de solidarité et de partage.`,
      },
      {
        id: 3,
        title: 'Unique interlocuteur des étudiants égyptiens en France',
        summary:
          'L\'association porte la voix des étudiants auprès des institutions françaises et égyptiennes et renforce les liens académiques, culturels et professionnels.',
        image: '/img/Gathering 1.jpeg',
        content: `Notre association vise aussi à représenter les étudiants égyptiens en France et à porter leur voix auprès des autorités égyptiennes et françaises. De manière plus générale, l'association a comme fin la construction d'un réseau fort et solidaire des jeunes égyptiens capables de tisser des liens entre la France et l'Egypte dans les différents domaines.`,
      },
      {
        id: 4,
        title: 'Une information disponible à tout moment',
        summary:
          'Guides pratiques, événements, offres et ressources utiles sont diffusés en continu pour faciliter la vie étudiante et les démarches administratives.',
        image: '/img/landmark 1.jpg',
        content: `Pour mener à bien sa fonction d'échange interculturel, permettant ainsi aux étudiants de participer à l'animation de la vie universitaire, l'ADEEF dispose d'une page et de groupes interactifs sur Facebook via lesquels son Conseil d'administration échange avec son public sur les questions, demandes ou propositions des étudiants.

Cette page est l'un des moyens d'information sur les « événements » organisés par l'ADEEF (activités scientifiques, culturels, de loisirs, etc.), des offres de stage et de travail, des bourses d'études en France et à l'étranger, des conférences et colloques sur des sujets qui peuvent intéresser les étudiants égyptiens en France, des sites utiles pour la recherche de logement, et des informations diverses.

Texte de test (pour vérifier le défilement dans la fenêtre "Learn more") :

L'ADEEF publie régulièrement des informations pratiques sur les démarches administratives (arrivée en France, ouverture de compte, assurance, titres de séjour, etc.), ainsi que des retours d'expérience d'étudiants et jeunes professionnels.

Nous partageons également des rappels et recommandations :
- Comment constituer un dossier de logement (pièces justificatives, garant, assurances)
- Comment éviter les arnaques les plus fréquentes (annonces frauduleuses, demandes de paiement suspectes)
- Où trouver des ressources utiles (associations, services universitaires, plateformes officielles)

En complément, des publications peuvent couvrir :
- La préparation d'entretiens (stages, alternance, premier emploi)
- La rédaction de CV et de lettres de motivation
- Les opportunités de networking et de mentorat

Important : les informations diffusées peuvent évoluer selon les règles en vigueur et les retours de terrain. Nous encourageons toujours les étudiants à recouper les informations avec les sources officielles et à demander conseil en cas de doute.

Pour tester davantage la barre de défilement, voici un paragraphe volontairement long : l'objectif est de s'assurer que la modale reste de taille fixe, que le fond de page ne scrolle pas, et que le contenu interne de la modale dispose bien d'un défilement fluide. Si vous pouvez lire cette phrase tout en gardant la page principale immobile, c'est que l'implémentation est correcte.

Dernière section de test :
L'ADEEF reste à l'écoute des questions récurrentes et consolide progressivement une base de connaissances. Si une réponse n'est pas immédiatement disponible, nous orientons vers les canaux appropriés et mettons à jour les ressources lorsque nécessaire.`,
      },
    ],
    []
  );

  const slides = useMemo(
    () => (remoteSlides.length > 0 ? remoteSlides : fallbackSlides),
    [remoteSlides, fallbackSlides]
  );

  const arrivalsCount = useCountUp(500, arrivalsPanel.isVisible, { duration: 1200, delay: 40 });
  const citiesCount = useCountUp(10, citiesPanel.isVisible, { duration: 1050, delay: 80 });
  const membersCount = useCountUp(7500, membersPanel.isVisible, { duration: 1350, delay: 120 });

  const donutIntroProgress = useProgressAnimation(arrivalsPanel.isVisible, {
    duration: 1150,
    delay: 80,
  });

  const membersChartIntroProgress = useProgressAnimation(membersPanel.isVisible, {
    duration: 980,
    delay: 140,
  });

  const targetArrivalValues = useMemo(
    () =>
      ARRIVAL_DISTRIBUTION.map((city) => (hiddenArrivalRegions.has(city.id) ? 0 : city.value)),
    [hiddenArrivalRegions]
  );

  const animatedArrivalValues = useTweenedList(targetArrivalValues, 680);

  const hasVisibleArrivalSegments = useMemo(
    () => targetArrivalValues.some((value) => value > 0),
    [targetArrivalValues]
  );

  const donutSegments = useMemo(() => {
    const safeValues = ARRIVAL_DISTRIBUTION.map((_, index) => Math.max(0, animatedArrivalValues[index] || 0));
    const activeTotal = safeValues.reduce((sum, value) => sum + value, 0);
    if (activeTotal <= 0) return [];

    let cumulativeLength = 0;
    return ARRIVAL_DISTRIBUTION.map((city, index) => {
      const ratio = safeValues[index] / activeTotal;
      const rawLength = ratio * DONUT_CIRCUMFERENCE * donutIntroProgress;
      const length = rawLength < 0.85 ? 0 : rawLength;
      const segment = {
        ...city,
        length,
        dashOffset: -cumulativeLength,
      };
      cumulativeLength += length;
      return segment;
    });
  }, [animatedArrivalValues, donutIntroProgress]);

  const donutRotation = useMemo(() => -90 + (donutIntroProgress * 360), [donutIntroProgress]);

  const membersChartGeometry = useMemo(() => {
    const plotWidth = MEMBERS_CHART.width - MEMBERS_CHART.marginLeft - MEMBERS_CHART.marginRight;
    const plotHeight = MEMBERS_CHART.height - MEMBERS_CHART.marginTop - MEMBERS_CHART.marginBottom;
    const baselineY = MEMBERS_CHART.height - MEMBERS_CHART.marginBottom;
    const axisSpan = MEMBERS_AXIS_MAX - MEMBERS_AXIS_MIN;
    const totalPoints = MEMBERS_MILESTONES.length;

    const points = MEMBERS_MILESTONES.map((milestone, index) => {
      const xRatio = totalPoints > 1 ? index / (totalPoints - 1) : 0;
      const x = MEMBERS_CHART.marginLeft + (plotWidth * xRatio);
      const normalized = (milestone.members - MEMBERS_AXIS_MIN) / axisSpan;
      const clamped = Math.max(0, Math.min(1, normalized));
      const targetY = baselineY - (clamped * plotHeight);
      const y = baselineY - ((baselineY - targetY) * membersChartIntroProgress);

      return {
        ...milestone,
        x,
        y,
      };
    });

    const yTicks = MEMBERS_AXIS_TICKS.map((tick) => {
      const normalized = (tick - MEMBERS_AXIS_MIN) / axisSpan;
      return {
        value: tick,
        y: baselineY - (Math.max(0, Math.min(1, normalized)) * plotHeight),
      };
    });

    return {
      baselineY,
      points,
      yTicks,
    };
  }, [membersChartIntroProgress]);

  const membersLinePath = useMemo(
    () => buildSmoothPath(membersChartGeometry.points),
    [membersChartGeometry.points]
  );

  const membersAreaPath = useMemo(
    () => buildAreaPath(membersChartGeometry.points, membersChartGeometry.baselineY),
    [membersChartGeometry.baselineY, membersChartGeometry.points]
  );

  const activeMilestone =
    Number.isInteger(activeMilestoneIndex) && membersChartGeometry.points[activeMilestoneIndex]
      ? membersChartGeometry.points[activeMilestoneIndex]
      : null;

  const activeMilestoneTooltip = useMemo(() => {
    if (!activeMilestone) return null;
    const horizontalInset = 78;
    const minY = MEMBERS_CHART.marginTop + 44;
    const maxY = MEMBERS_CHART.height - MEMBERS_CHART.marginBottom - 12;
    const x = Math.max(horizontalInset, Math.min(MEMBERS_CHART.width - horizontalInset, activeMilestone.x));
    const y = Math.max(minY, Math.min(maxY, activeMilestone.y));

    return {
      left: `${(x / MEMBERS_CHART.width) * 100}%`,
      top: `${(y / MEMBERS_CHART.height) * 100}%`,
      placeBelow: activeMilestone.y < MEMBERS_CHART.marginTop + 54,
    };
  }, [activeMilestone]);

  useEffect(() => {
    let cancelled = false;

    const loadSlides = async () => {
      try {
        const response = await api.carousel.listPublicSlides({ limit: 100 });
        const payload = response?.data;
        const rows = payload?.ok ? (payload?.data || []) : (payload?.data || []);
        if (!Array.isArray(rows) || rows.length === 0) return;

        const mapped = rows
          .map((row) => {
            const body = String(row.body || '').trim();
            if (!row.title || !body || !row.image_url) return null;

            return {
              id: Number(row.id),
              title: String(row.title || '').trim(),
              summary: body.length > 210 ? `${body.slice(0, 207)}...` : body,
              image: resolveMediaUrl(row.image_url) || row.image_url,
              content: body,
            };
          })
          .filter(Boolean);

        if (!cancelled && mapped.length > 0) {
          setRemoteSlides(mapped);
        }
      } catch (error) {
        console.error('Failed to load carousel slides:', error);
      }
    };

    loadSlides();
    return () => {
      cancelled = true;
    };
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  // Auto-advance slides every 7 seconds
  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const interval = setInterval(nextSlide, 7000);
    return () => clearInterval(interval);
  }, [nextSlide, slides.length]);

  useEffect(() => {
    if (currentSlide < slides.length) return;
    setCurrentSlide(0);
  }, [currentSlide, slides.length]);

  useEffect(() => {
    const node = carouselSectionRef.current;
    if (!node) return;

    if (typeof IntersectionObserver === 'undefined') {
      setIsCarouselInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        setIsCarouselInView(entry.isIntersecting);
      },
      { threshold: 0.35 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!modalSlide) return;
    if (typeof document === 'undefined') return;

    bodyOverflowRef.current = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = bodyOverflowRef.current ?? '';
    };
  }, [modalSlide]);

  const openLearnMore = (slide) => {
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    setClosingModal(false);
    carouselAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setModalSlide(slide);
  };

  const closeLearnMore = () => {
    if (!modalSlide) return;
    setClosingModal(true);
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = window.setTimeout(() => {
      setModalSlide(null);
      setClosingModal(false);
    }, 260);
  };

  const toggleArrivalRegion = useCallback((cityId) => {
    setHiddenArrivalRegions((previous) => {
      const next = new Set(previous);
      if (next.has(cityId)) {
        next.delete(cityId);
      } else {
        next.add(cityId);
      }
      return next;
    });
  }, []);

  const updateArrivalTooltip = useCallback((event, segment) => {
    const svgElement = event.currentTarget.ownerSVGElement;
    if (!svgElement) return;
    const wrapElement = event.currentTarget.closest('.stats-donut-wrap');
    const bounds = (wrapElement || svgElement).getBoundingClientRect();
    const rawX = event.clientX - bounds.left;
    const rawY = event.clientY - bounds.top;
    const horizontalInset = Math.min(86, Math.max(20, (bounds.width / 2) - 10));
    const verticalInset = Math.min(74, Math.max(18, (bounds.height / 2) - 10));
    const x = Math.max(horizontalInset, Math.min(bounds.width - horizontalInset, rawX));
    const y = Math.max(verticalInset, Math.min(bounds.height - 12, rawY));

    setActiveArrivalSlice({
      id: segment.id,
      label: segment.label,
      value: segment.value,
      color: segment.color,
      x,
      y,
      placeBelow: rawY < (verticalInset + 12),
    });
  }, []);

  const clearArrivalTooltip = useCallback(() => {
    setActiveArrivalSlice(null);
  }, []);

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">
            Bienvenue chez <span className="highlight">ADEEF</span>
          </h1>
          <p className="hero-subtitle">
            Association Des Étudiants Égyptiens en France
          </p>
          <p className="hero-description">
            Nous accompagnons les étudiants égyptiens dans leur parcours en France, 
            en simplifiant les démarches et en offrant un soutien personnalisé.
          </p>
          <div className="hero-buttons">
            <Link to="/demander-logement" className="bubbles about-bubbles-cta">
              <span className="text">Demander un logement</span>
            </Link>
            <Link to="/guide-adeef" className="bubbles about-bubbles-cta">
              <span className="text">Découvrir le guide</span>
            </Link>
          </div>
        </div>
      </section>

      <main className="landing-main">
        <div className="container">
          <div className="landing-flex">
            <div className="landing-col landing-col-main">
              {/* Carousel Section */}
              <Reveal as="section" className="carousel-section" variant="up">
                <div
                  className={`carousel-shell ${isCarouselInView ? 'is-in-view' : ''}`}
                  ref={carouselSectionRef}
                >
                  <div className="carousel-slides" ref={carouselAnchorRef}>
                    {slides.map((slide, index) => (
                      <article
                        key={slide.id}
                        className={`carousel-showcase ${currentSlide === index ? 'active' : ''}`}
                        role="group"
                        aria-roledescription="slide"
                        aria-label={`${index + 1} sur ${slides.length} - ${slide.title}`}
                        aria-hidden={currentSlide !== index}
                      >
                        <div className="carousel-copy">
                          <p className="carousel-kicker">Ce que nous faisons</p>
                          <h3 className="carousel-title">{slide.title}</h3>
                          <p className="carousel-summary">{slide.summary}</p>

                          <button
                            type="button"
                            className="carousel-learn-more"
                            onClick={() => openLearnMore(slide)}
                            aria-label={`En savoir plus : ${slide.title}`}
                            tabIndex={currentSlide === index ? 0 : -1}
                          >
                            <span className="carousel-learn-more-text">En savoir plus</span>
                            <span className="carousel-learn-more-line" aria-hidden="true" />
                          </button>
                        </div>

                        <div className="carousel-media-column">
                          <div className="carousel-media-underlay" aria-hidden="true" />
                          <img
                            className="carousel-media-image"
                            src={slide.image}
                            alt={slide.title}
                            loading={index === 0 ? 'eager' : 'lazy'}
                          />
                        </div>
                      </article>
                    ))}
                  </div>

                  <div className="carousel-controls" aria-label="Navigation du carrousel">
                    <button
                      type="button"
                      className="carousel-nav-btn"
                      onClick={prevSlide}
                      aria-label="Slide précédente"
                    >
                      ‹
                    </button>
                    <span className="carousel-index-current">{String(currentSlide + 1).padStart(2, '0')}</span>
                    <span className="carousel-index-separator" aria-hidden="true" />
                    <span className="carousel-index-total">{String(slides.length).padStart(2, '0')}</span>
                    <button
                      type="button"
                      className="carousel-nav-btn"
                      onClick={nextSlide}
                      aria-label="Slide suivante"
                    >
                      ›
                    </button>
                  </div>
                </div>

                {modalSlide &&
                  typeof document !== 'undefined' &&
                  createPortal(
                    <div className="adeef-carousel-modal-overlay" onClick={closeLearnMore} role="presentation">
                      <motion.div
                        className="adeef-carousel-modal"
                        onClick={(e) => e.stopPropagation()}
                        initial={{ opacity: 0, y: 18, scale: 0.98 }}
                        animate={
                          closingModal
                            ? { opacity: 0, y: 18, scale: 0.98 }
                            : { opacity: 1, y: 0, scale: 1 }
                        }
                        transition={{ duration: 0.26, ease: [0.65, 0, 0.076, 1] }}
                        role="dialog"
                        aria-modal="true"
                        aria-label={modalSlide.title}
                      >
                        <div className="adeef-carousel-modal-header">
                          <button type="button" className="adeef-carousel-back" onClick={closeLearnMore}>
                            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                              <path
                                d="M15 18l-6-6 6-6"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            Retour
                          </button>
                        </div>

                        <div className="adeef-carousel-modal-content">
                          <h3 className="adeef-carousel-modal-title">{modalSlide.title}</h3>
                          <div className="adeef-carousel-modal-text">
                            <p>{modalSlide.content}</p>
                            {modalSlide.founders && (
                              <div className="founders-section">
                                <h4>Les membres fondateurs de l'ADEEF</h4>
                                <ol className="founders-list">
                                  {modalSlide.founders.map((founder, idx) => (
                                    <li key={idx}>{founder}</li>
                                  ))}
                                </ol>
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    </div>,
                    document.body
                  )}
              </Reveal>

              {/* Statistics Section */}
              <section className="stats-section adeef-fullbleed" aria-label="Nos statistiques">
                <div className="stats-section-content">
                  <h2 className="stats-headline" aria-label="Nos statistiques">
                    <span className="stats-headline-word">Nos Statistiques</span>
                  </h2>

                  <div className="stats-panels-grid">
                    <Reveal as="article" className="stats-panel" variant="up" delay={100}>
                      <div ref={arrivalsPanel.ref} className="stats-panel-shell">
                        <div className="stats-panel-chart stats-panel-chart--arrivals">
                          <div className="stats-legend" role="toolbar" aria-label="Filtrer les villes du graphique circulaire">
                            {ARRIVAL_DISTRIBUTION.map((city) => {
                              const isHidden = hiddenArrivalRegions.has(city.id);
                              return (
                                <button
                                  key={city.id}
                                  type="button"
                                  className={`stats-legend-item ${isHidden ? 'is-hidden' : ''}`}
                                  onClick={() => toggleArrivalRegion(city.id)}
                                  aria-pressed={!isHidden}
                                  aria-label={`${city.label}: ${formatDistributionValue(city.value)}. ${isHidden ? 'Afficher' : 'Masquer'}`}
                                >
                                  <span
                                    className="stats-legend-swatch"
                                    style={{ backgroundColor: city.color }}
                                    aria-hidden="true"
                                  />
                                  <span className="stats-legend-text">{city.label}</span>
                                </button>
                              );
                            })}
                          </div>

                          <div className="stats-donut-wrap">
                            <svg
                              className="stats-donut-svg"
                              viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`}
                              role="img"
                              aria-label="Répartition des nouveaux arrivants par ville"
                            >
                              <circle
                                className="stats-donut-track"
                                cx={DONUT_CENTER}
                                cy={DONUT_CENTER}
                                r={DONUT_RADIUS}
                              />
                              <g transform={`rotate(${donutRotation} ${DONUT_CENTER} ${DONUT_CENTER})`}>
                                {donutSegments.filter((segment) => segment.length > 0).map((segment) => (
                                  <circle
                                    key={segment.id}
                                    className="stats-donut-segment"
                                    cx={DONUT_CENTER}
                                    cy={DONUT_CENTER}
                                    r={DONUT_RADIUS}
                                    stroke={segment.color}
                                    strokeDasharray={`${segment.length} ${Math.max(0, DONUT_CIRCUMFERENCE - segment.length)}`}
                                    strokeDashoffset={segment.dashOffset}
                                    strokeLinecap="butt"
                                    onMouseEnter={(event) => updateArrivalTooltip(event, segment)}
                                    onMouseMove={(event) => updateArrivalTooltip(event, segment)}
                                    onMouseLeave={clearArrivalTooltip}
                                  />
                                ))}
                              </g>
                            </svg>

                            {activeArrivalSlice && (
                              <div
                                className={`stats-donut-tooltip ${activeArrivalSlice.placeBelow ? 'is-below' : ''}`}
                                style={{
                                  left: `${activeArrivalSlice.x}px`,
                                  top: `${activeArrivalSlice.y}px`,
                                }}
                                role="status"
                              >
                                <span
                                  className="stats-donut-tooltip-dot"
                                  style={{ backgroundColor: activeArrivalSlice.color }}
                                  aria-hidden="true"
                                />
                                <span>
                                  {activeArrivalSlice.label}: {formatDistributionValue(activeArrivalSlice.value)}
                                </span>
                              </div>
                            )}

                            {!hasVisibleArrivalSegments && (
                              <p className="stats-donut-empty">Aucune ville active</p>
                            )}
                          </div>
                        </div>

                        <footer className="stats-panel-footer">
                          <div className="stats-panel-count">
                            <span className="stats-panel-count-value">{formatPlainInteger(arrivalsCount)}</span>
                            <span className="stats-panel-count-unit">-</span>
                          </div>
                          <p className="stats-panel-caption">Nouveaux arrivants en France en 2025-2026</p>
                        </footer>
                      </div>
                    </Reveal>

                    <Reveal as="article" className="stats-panel stats-panel--map" variant="up" delay={190}>
                      <div ref={citiesPanel.ref} className="stats-panel-shell">
                        <div className="stats-panel-chart stats-panel-chart--map">
                          <StatsFranceMap />
                        </div>

                        <footer className="stats-panel-footer">
                          <div className="stats-panel-count">
                            <span className="stats-panel-count-value">{formatPlainInteger(citiesCount)}</span>
                            <span className="stats-panel-count-unit stats-panel-count-unit--word">Villes</span>
                          </div>
                          <p className="stats-panel-caption">Présence de l'ADEEF dans les différentes villes</p>
                        </footer>
                      </div>
                    </Reveal>

                    <Reveal as="article" className="stats-panel" variant="up" delay={280}>
                      <div ref={membersPanel.ref} className="stats-panel-shell">
                        <div className="stats-panel-chart stats-panel-chart--members">
                          <div
                            className="members-chart-wrap"
                            onMouseLeave={() => setActiveMilestoneIndex(null)}
                            onBlur={(event) => {
                              if (!event.currentTarget.contains(event.relatedTarget)) {
                                setActiveMilestoneIndex(null);
                              }
                            }}
                          >
                            <svg
                              className="members-chart-svg"
                              viewBox={`0 0 ${MEMBERS_CHART.width} ${MEMBERS_CHART.height}`}
                              preserveAspectRatio="none"
                              role="img"
                              aria-label="Croissance des membres ADEEF"
                            >
                              <defs>
                                <linearGradient id="membersAreaGradient" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor="#95afd8" stopOpacity="0.78" />
                                  <stop offset="100%" stopColor="#95afd8" stopOpacity="0.38" />
                                </linearGradient>
                              </defs>

                              {membersChartGeometry.yTicks.map((tick) => (
                                <g key={tick.value}>
                                  <line
                                    className="members-grid-line"
                                    x1={MEMBERS_CHART.marginLeft}
                                    x2={MEMBERS_CHART.width - MEMBERS_CHART.marginRight}
                                    y1={tick.y}
                                    y2={tick.y}
                                  />
                                  <text
                                    className="members-axis-label members-axis-label--y"
                                    x={MEMBERS_CHART.marginLeft - 10}
                                    y={tick.y + 4}
                                  >
                                    {tick.value}
                                  </text>
                                </g>
                              ))}

                              <line
                                className="members-axis-line"
                                x1={MEMBERS_CHART.marginLeft}
                                x2={MEMBERS_CHART.width - MEMBERS_CHART.marginRight}
                                y1={membersChartGeometry.baselineY}
                                y2={membersChartGeometry.baselineY}
                              />

                              {membersAreaPath && (
                                <path
                                  className="members-area"
                                  d={membersAreaPath}
                                  style={{ opacity: membersChartIntroProgress }}
                                />
                              )}

                              {membersLinePath && (
                                <path
                                  className="members-line"
                                  d={membersLinePath}
                                  pathLength="1"
                                  style={{
                                    strokeDasharray: 1,
                                    strokeDashoffset: 1 - membersChartIntroProgress,
                                  }}
                                />
                              )}

                              {membersChartGeometry.points.map((point, index) => {
                                const isActive = activeMilestoneIndex === index;
                                return (
                                  <g key={point.year}>
                                    <circle
                                      className={`members-point-core ${isActive ? 'is-active' : ''}`}
                                      cx={point.x}
                                      cy={point.y}
                                      r={isActive ? 6 : 4}
                                    />
                                    <circle
                                      className="members-point-hit"
                                      cx={point.x}
                                      cy={point.y}
                                      r={15}
                                      tabIndex={0}
                                      onMouseEnter={() => setActiveMilestoneIndex(index)}
                                      onFocus={() => setActiveMilestoneIndex(index)}
                                      onClick={() => setActiveMilestoneIndex(index)}
                                      aria-label={`${point.year}: ${formatPlainInteger(point.members)} membres`}
                                    />
                                  </g>
                                );
                              })}

                              {membersChartGeometry.points.map((point) => (
                                <text
                                  key={`year-${point.year}`}
                                  className="members-axis-label members-axis-label--x"
                                  x={point.x}
                                  y={MEMBERS_CHART.height - 8}
                                >
                                  {point.year}
                                </text>
                              ))}
                            </svg>

                            {activeMilestone && activeMilestoneTooltip && (
                              <div
                                className={`members-point-tooltip ${activeMilestoneTooltip.placeBelow ? 'is-below' : ''}`}
                                style={{
                                  left: activeMilestoneTooltip.left,
                                  top: activeMilestoneTooltip.top,
                                }}
                                role="status"
                              >
                                <p className="members-point-tooltip-year">{activeMilestone.year}</p>
                                <p className="members-point-tooltip-value">
                                  Membres: {formatPlainInteger(activeMilestone.members)}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        <footer className="stats-panel-footer">
                          <div className="stats-panel-count">
                            <span className="stats-panel-count-value">{formatPlainInteger(membersCount)}</span>
                            <span className="stats-panel-count-unit stats-panel-count-unit--word">Membres</span>
                          </div>
                          <p className="stats-panel-caption">Membres de l'ADEEF en France</p>
                        </footer>
                      </div>
                    </Reveal>
                  </div>
                </div>
              </section>

            </div>

          </div>



          {/* Team Pyramid Section */}
          <Reveal as="section" className="team-tree-section team-pyramid-section" variant="up" delay={60}>
            <h2 className="section-title">Notre équipe</h2>
            <TeamPyramid />
          </Reveal>

        </div>
      </main>
    </div>
  );
};

export default About;
